<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  requiredBodyKeysFilled,
  isAutoResolvableNamedVar,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  // Upstream #15255. Falso (inbox WhatsApp Cloud): manda o corpo CRU do
  // template + processed_params, e o servidor substitui em
  // Whatsapp::TemplateContentRendererService SEM passar por Liquid — que
  // interpretaria {{1}}/{{2}} como expressão numérica e apagaria o valor.
  // Verdadeiro (inbox de API, sem provedor Meta para renderizar): não há quem
  // substitua no servidor, então o cliente manda o texto já renderizado.
  sendRenderedContent: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'sendMessage',
  'resetTemplate',
  'back',
  // Emitted with the DEEP validity (true = all required vars filled) so parents
  // that render their OWN submit button (e.g. FollowUpModal, Options API where
  // $refs are non-reactive) can gate on it reactively.
  'validityChange',
]);

const { t } = useI18n();

const processedParams = ref({});

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

// NAMED templates ({{contact_name}}) have auto-resolvable variables that the
// backend fills server-side (Whatsapp::NamedTemplateVarsResolver). The send
// form must not force the agent to fill those — otherwise the feature is
// unreachable.
const isNamed = computed(() => props.template.parameter_format === 'NAMED');

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

// A media header parameter must be a real, public https URL — WhatsApp fetches
// it to build the message. A blank value, a placeholder ("."/"teste"), a host
// without scheme ("unsplash.com") or a non-https/localhost URL all break the
// send (local "Invalid URL scheme" crash, Meta #132018 "media ID or link must
// be present"), so require a parseable https URL here — matching the hint and
// Meta's requirement — instead of only checking presence.
// IPv4 loopback/privados/link-local — o WhatsApp não alcança esses hosts.
const PRIVATE_HOST =
  /^(127\.|10\.|192\.168\.|169\.254\.|172\.(1[6-9]|2\d|3[01])\.)/;

const isValidMediaUrl = url => {
  if (!url) return false;
  try {
    const { protocol, hostname } = new URL(url);
    // https + host com domínio (tem ponto), não loopback/localhost nem IP
    // privado. O WhatsApp busca essa URL. Validação de "público" 100% (DNS) é
    // impraticável no cliente; a Meta cobre o resto no envio.
    if (protocol !== 'https:' || !hostname.includes('.')) return false;
    return !PRIVATE_HOST.test(hostname);
  } catch {
    return false;
  }
};

const isFormInvalid = computed(() => {
  if (!hasVariables.value && !hasMediaHeader.value) return false;

  if (
    hasMediaHeader.value &&
    !isValidMediaUrl(processedParams.value.header?.media_url)
  ) {
    return true;
  }

  // For NAMED templates, auto-resolvable vars (contact_name, agent_name, …)
  // may be left blank — the backend fills them. Only require the rest.
  if (
    hasVariables.value &&
    !requiredBodyKeysFilled(processedParams.value.body, isNamed.value)
  ) {
    return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: props.sendRenderedContent
      ? renderedTemplate.value
      : bodyText.value,
    // O transcrito otimista mostra o texto renderizado mesmo quando `message`
    // vai cru — quem lê isso é createPendingMessage (helper/commons.js).
    pendingMessageContent: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      content_mode: props.sendRenderedContent ? 'rendered' : 'raw_template',
      processed_params: processedParams.value,
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

const emitValidity = () => emit('validityChange', !isFormInvalid.value);

onMounted(() => {
  initializeTemplateParameters();
  emitValidity();
});

watch(isFormInvalid, emitValidity);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
    // Re-emit validity: when the component is reused across templates (parent
    // swaps :template without remounting), isFormInvalid may not change (e.g. an
    // already-valid → no-variable template), so the watch(isFormInvalid) above
    // would not fire and a parent gating on the event would get stuck.
    emitValidity();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  // isFormInvalid is the DEEP validity (blank body vars / media URL / button
  // params) — v$ only checks the top-level processedParams keys exist. Consumers
  // that gate their own "next"/submit must use this, not v$.$invalid.
  isFormInvalid,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <div class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateMediaUrl"
          />
        </div>
        <p
          v-if="
            processedParams.header?.media_url &&
            !isValidMediaUrl(processedParams.header.media_url)
          "
          class="mb-2.5 text-xs text-n-ruby-9"
        >
          {{ t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_INVALID') }}
        </p>
        <p v-else class="mb-2.5 text-xs text-n-slate-11">
          {{ t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_HINT') }}
        </p>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <!-- NAMED templates: explain that some vars resolve automatically -->
        <p
          v-if="isNamed"
          class="mb-2.5 text-xs text-n-slate-11 flex items-start gap-1.5"
        >
          <span class="i-lucide-sparkles size-3.5 mt-0.5 shrink-0" />
          {{ $t('WHATSAPP_TEMPLATES.PARSER.AUTO_RESOLVED_HINT') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center gap-2 mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
          <span
            v-if="isNamed && isAutoResolvableNamedVar(key)"
            class="text-[10px] font-bold uppercase tracking-wide text-n-brand bg-n-brand/10 border border-n-brand/20 px-2 py-1 rounded-md shrink-0"
          >
            {{ $t('WHATSAPP_TEMPLATES.PARSER.AUTO_RESOLVED_BADGE') }}
          </span>
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
